(() => {
  const root = document.querySelector('[data-mtu-root]');
  if (!root) return;

  const loadingEl = root.querySelector('[data-mtu-loading]');
  const emptyEl = root.querySelector('[data-mtu-empty]');
  const errorEl = root.querySelector('[data-mtu-error]');
  const cards = Array.from(root.querySelectorAll('[data-mtu-card]'));
  const isRich = root.hasAttribute('data-mtu-rich') || root.classList.contains('mtu--rich');

  const storeDomain = root.dataset.storeDomain || 'nutrition-su.myshopify.com';
  const apiVersion = root.dataset.apiVersion || '2025-10';
  const token = root.dataset.storefrontToken || '76d349f53add8fb2f0560350acfc089f';
  const sellingPlanId = root.dataset.sellingPlanId || '3487203430';
  const allowedPricingModes = new Set(['first_cycle', 'always_15', 'first_cycle_60_20']);
  const rawPricingMode = root.dataset.pricingMode;
  const pricingMode = allowedPricingModes.has(rawPricingMode) ? rawPricingMode : 'first_cycle';
  const upsellPath = root.dataset.upsellPath || '/pages/ume-subscription-up';

  const params = new URLSearchParams(window.location.search);
  const itemsParam = params.get('items');
  const nameParam = params.get('name') || params.get('customer_name');
  const customerNameEl = root.querySelector('[data-mtu-customer-name]');

  const customerName = (nameParam || root.dataset.customerName || '').trim();
  if (customerNameEl && customerName) {
    const suffix = customerName === 'あなた' || customerName.endsWith('様') ? '' : '様';
    customerNameEl.textContent = `${customerName}${suffix}`;
  }

  const showState = (state) => {
    if (loadingEl) loadingEl.hidden = state !== 'loading';
    if (emptyEl) emptyEl.hidden = state !== 'empty';
    if (errorEl) errorEl.hidden = state !== 'error';
    if (state === 'ready') {
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
    }
  };

  const formatPrice = (amount, currencyCode) => {
    if (!amount || !currencyCode) return '価格未取得';
    try {
      return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: currencyCode }).format(Number(amount));
    } catch (e) {
      return `${amount} ${currencyCode}`;
    }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));

  const supplementDescriptions = [
    { keywords: ['マグネシウム'], text: '筋肉や神経の機能維持、エネルギー代謝に欠かせないミネラル' },
    { keywords: ['ヘム鉄', '鉄'], text: '酸素を全身に運ぶ重要ミネラル' },
    { keywords: ['亜鉛', 'Zn'], text: '細胞分裂や酵素反応を支える必須ミネラル' },
    { keywords: ['ビタミンD', 'Vitamin D'], text: 'カルシウム吸収を助ける脂溶性ビタミン' },
    { keywords: ['ビタミンC', 'Vitamin C'], text: '美容と健康維持、抗酸化サポートに役立つ水溶性ビタミン' },
    { keywords: ['ビタミンB', 'Vitamin B', 'B群'], text: 'エネルギー産生を支える補酵素群' },
    { keywords: ['葉酸'], text: '赤血球の形成や細胞の生まれ変わりを支えるビタミンB群の一種' },
    { keywords: ['クリルオイル', 'DHA', 'EPA', 'オメガ3', 'オメガ３', 'オメガ3脂肪酸', 'Omega'], text: '体内で作れない必須脂肪酸' },
    { keywords: ['コエンザイム', 'コエンザイムQ10', 'CoQ10', 'Coenzyme', 'ユビキノン'], text: '細胞の発電所を支える補酵素' },
    { keywords: ['乳酸菌', 'プロバイオティクス'], text: '腸内環境を整え、内側からの健康維持をサポートする善玉菌' },
    { keywords: ['カルシウム', 'Ca'], text: '骨や歯の形成を支え、健康な体づくりに欠かせないミネラル' },
    { keywords: ['マルチビタミン', 'マルチミネラル'], text: '不足しがちなビタミン・ミネラルをまとめて補うベースサプリ' }
  ];

  const getSupplementDescription = (productTitle, variantTitle, fallbackDescription) => {
    const target = `${productTitle || ''} ${variantTitle || ''}`.toLowerCase();
    const matched = supplementDescriptions.find((item) =>
      item.keywords.some((keyword) => target.includes(keyword.toLowerCase()))
    );
    return matched ? matched.text : summarizeText(fallbackDescription);
  };

  const richSupplementMeta = [
    {
      key: 'zinc-copper',
      keywords: ['亜鉛', '銅', 'Zn'],
      description: '亜鉛分子栄養学の知見を支える必須ミネラル',
      functionLabel: '亜鉛栄養機能食品'
    },
    {
      key: 'vitamin-d-omega3',
      keywords: ['ビタミンD', 'Vitamin D'],
      description: '1粒に1,000IUのビタミンDとノルウェー産サーモンオイル由来のオメガ-3を配合',
      functionLabel: 'ビタミンD栄養機能食品'
    },
    {
      key: 'vitamin-b',
      keywords: ['ビタミンB', 'Vitamin B', 'B群'],
      description: '8種のビタミンB群をバランス良く高配合',
      functionLabel: 'ビタミンB栄養機能食品'
    },
    {
      key: 'heme-iron',
      keywords: ['ヘム鉄', '鉄'],
      description: '胃にやさしいヘム鉄と7種のビタミン・ミネラルを配合',
      functionLabel: '鉄栄養機能食品'
    },
    {
      key: 'ginkgo-omega3',
      keywords: ['イチョウ葉', '銀杏葉', 'オメガ3', 'オメガ３', 'Omega', 'DHA', 'EPA'],
      description: '国産イチョウ葉エキスにオメガ-3（アマニ油）をプラス',
      functionLabel: '栄養機能食品'
    },
    {
      key: 'coq10',
      keywords: ['コエンザイム', 'コエンザイムQ10', 'CoQ10', 'Coenzyme', 'ユビキノン'],
      description: '国産発酵法コエンザイムQ10と植物由来ビタミンEを配合',
      functionLabel: '栄養機能食品'
    }
  ];

  const getRichSupplementMeta = (productTitle, variantTitle) => {
    const target = `${productTitle || ''} ${variantTitle || ''}`.toLowerCase();
    return richSupplementMeta.find((item) =>
      item.keywords.some((keyword) => target.includes(keyword.toLowerCase()))
    ) || richSupplementMeta[0];
  };

  const setRichSupplementDetails = (productTitle, variantTitle) => {
    if (!isRich) return;
    const meta = getRichSupplementMeta(productTitle, variantTitle);
    const panels = root.querySelectorAll('[data-mtu-rich-detail-panel]');
    panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-key') !== meta.key;
    });
  };

  const summarizeText = (value, maxLength = 90) => {
    const text = String(value || '').replace(/■\s*商品説明/g, '').replace(/商品説明/g, '').replace(/\s+/g, ' ').trim();
    if (!text) return '今のあなたに必要な栄養素を補うためのサプリメントです。';
    return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
  };

  const parseItems = (raw) => {
    if (!raw) return [];
    return raw.split(',').map((chunk) => {
      const [idRaw, qtyRaw] = chunk.split(':');
      const id = (idRaw || '').trim();
      if (!/^\d+$/.test(id)) return null;
      const qty = parseInt((qtyRaw || '').trim(), 10);
      return { id, qty: Number.isFinite(qty) && qty > 0 ? qty : 1 };
    }).filter(Boolean);
  };

  const subToRegularMap = {
    '43456652116070': '42836103561318',
    '43456652148838': '42835651788902',
    '43456651591782': '42836121288806',
    '43456651821158': '42836113981542',
    '43456651755622': '42836119093350',
    '43456651853926': '42836110508134',
  };

  const firstCycleSubscriptionPriceMap = {
    '43456652148838': { regular: 3180, first: 1620, recurring: 2700 },
    '43456652116070': { regular: 2880, first: 1470, recurring: 2450 },
    '43456651853926': { regular: 4480, first: 2280, recurring: 3800 },
    '43456651821158': { regular: 3880, first: 1980, recurring: 3300 },
    '43456651755622': { regular: 5680, first: 2900, recurring: 4830 },
    '43456651591782': { regular: 3880, first: 1980, recurring: 3300 },
  };

  const always15SubscriptionPriceMap = {
    '43456652148838': { regular: 3180, first: 2700, recurring: 2700 },
    '43456652116070': { regular: 2880, first: 2450, recurring: 2450 },
    '43456651853926': { regular: 4480, first: 3800, recurring: 3800 },
    '43456651821158': { regular: 3880, first: 3300, recurring: 3300 },
    '43456651755622': { regular: 5680, first: 4830, recurring: 4830 },
    '43456651591782': { regular: 3880, first: 3300, recurring: 3300 },
  };

  const firstCycle6020SubscriptionPriceMap = {
    '43456652148838': { regular: 3180, first: 1270, recurring: 2540 },
    '43456652116070': { regular: 2880, first: 1150, recurring: 2300 },
    '43456651853926': { regular: 4480, first: 1790, recurring: 3580 },
    '43456651821158': { regular: 3880, first: 1550, recurring: 3100 },
    '43456651755622': { regular: 5680, first: 2270, recurring: 4540 },
    '43456651591782': { regular: 3880, first: 1550, recurring: 3100 },
  };

  const subscriptionPriceMaps = {
    first_cycle: firstCycleSubscriptionPriceMap,
    always_15: always15SubscriptionPriceMap,
    first_cycle_60_20: firstCycle6020SubscriptionPriceMap,
  };
  const subscriptionPriceMap = subscriptionPriceMaps[pricingMode] || firstCycleSubscriptionPriceMap;

  const regularToSubMap = Object.fromEntries(
    Object.entries(subToRegularMap).map(([subId, regularId]) => [regularId, subId])
  );

  const selectedSellingPlanIds = new Map();

  const normalizeItems = (planItems, variantMap) => (
    planItems.map((item) => ({
      ...item,
      id: variantMap[item.id] || item.id
    }))
  );

  const buildSwitchUrl = (pathname, mappedItems) => {
    if (!mappedItems || mappedItems.length === 0) return null;
    const targetParams = new URLSearchParams();
    targetParams.set('items', mappedItems.join(','));
    if (nameParam) {
      targetParams.set(params.has('customer_name') ? 'customer_name' : 'name', nameParam);
    }
    return `${pathname}?${targetParams.toString()}`;
  };

  const setSecondaryLink = (link, href) => {
    if (!link || !href) return;
    link.setAttribute('href', href);
    link.removeAttribute('aria-disabled');
    link.style.display = 'block';
  };

  const buildUpsellUrl = () => {
    const upsellParams = new URLSearchParams();
    upsellParams.set('items', items.slice(0, 3).map((item) => `${item.id}:${item.qty}`).join(','));
    if (nameParam) {
      upsellParams.set(params.has('customer_name') ? 'customer_name' : 'name', nameParam);
    }
    return `${upsellPath}?${upsellParams.toString()}`;
  };

  const getNumericSellingPlanId = (gid) => {
    const match = String(gid || '').match(/SellingPlan\/(\d+)/);
    return match ? match[1] : gid;
  };

  const getAllocationFirstPrice = (allocation) => {
    const amount = allocation?.node?.priceAdjustments?.[0]?.price?.amount;
    const price = Number(amount);
    return Number.isFinite(price) ? Math.round(price) : null;
  };

  const findTargetAllocation = (variantId, allocations) => {
    const expectedPrices = subscriptionPriceMap[variantId];
    if (expectedPrices) {
      const fixedPriceAllocation = allocations.find((edge) =>
        getAllocationFirstPrice(edge) === expectedPrices.first
      );
      if (fixedPriceAllocation) return fixedPriceAllocation;
    }

    if (pricingMode !== 'first_cycle') return null;

    return allocations.find((edge) =>
      edge.node.sellingPlan.id.includes(sellingPlanId)
    ) || allocations[0];
  };

  const isPlainLeftClick = (event) => (
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
  );

  const items = normalizeItems(parseItems(itemsParam), regularToSubMap);
  if (items.length === 0) {
    showState('empty');
    cards.forEach((card) => card.classList.add('is-hidden'));
    return;
  }

  const visiblePlans = ['ume'];

  cards.forEach((card) => {
    const plan = card.getAttribute('data-plan');
    if (!visiblePlans.includes(plan)) card.classList.add('is-hidden');
  });

  const planItemsMap = new Map();
  cards.forEach((card) => {
    const plan = card.getAttribute('data-plan');
    if (!visiblePlans.includes(plan)) return;
    const indices = (card.getAttribute('data-indices') || '')
      .split(',').map((v) => parseInt(v, 10)).filter((v) => Number.isFinite(v));
    planItemsMap.set(plan, indices.map((idx) => items[idx]).filter(Boolean));
  });

  // 定期購入カートURL（単一商品用）
  const buildSubscriptionCartUrl = (planItems) => {
    if (!planItems || planItems.length === 0) return null;
    const item = planItems[0];
    const itemSellingPlanId = selectedSellingPlanIds.get(item.id);
    if (!itemSellingPlanId) return null;

    const cartParams = new URLSearchParams();
    cartParams.set('id', item.id);
    cartParams.set('quantity', String(item.qty));
    cartParams.set('selling_plan', itemSellingPlanId);
    cartParams.set('return_to', '/cart');
    return `/cart/add?${cartParams.toString()}`;
  };

  // カートボタンの初期設定
  cards.forEach((card) => {
    const plan = card.getAttribute('data-plan');
    if (!visiblePlans.includes(plan)) return;
    const planItems = planItemsMap.get(plan) || [];
    const cartButton = card.querySelector('[data-mtu-cart-button]');
    const regularLink = card.querySelector('[data-mtu-regular-link]');

    if (cartButton) {
      if (items.length >= 3) {
        cartButton.setAttribute('href', buildUpsellUrl());
        cartButton.removeAttribute('aria-disabled');
        const buttonLabel = cartButton.querySelector('[data-mtu-button-label]');
        if (buttonLabel) buttonLabel.textContent = 'サプリ購入へ進む';
      } else {
        cartButton.setAttribute('href', '#');
        cartButton.setAttribute('aria-disabled', 'true');
      }
    }

    if (regularLink && planItems.length > 0) {
      const regularItems = planItems
        .map((item) => subToRegularMap[item.id] ? `${subToRegularMap[item.id]}:${item.qty}` : null)
        .filter(Boolean);
      setSecondaryLink(regularLink, buildSwitchUrl('/pages/ume', regularItems));
    }
  });

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-mtu-regular-link]') : null;
    if (!target || !isPlainLeftClick(event)) return;

    const href = target.getAttribute('href');
    if (!href || href === '#' || target.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    window.location.assign(target.href);
  }, true);

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-mtu-cart-button]') : null;
    if (!target) return;
    const card = target.closest('[data-mtu-card]');
    if (!card) return;
    if (items.length >= 3) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(buildUpsellUrl());
      return;
    }
    const cartUrl = buildSubscriptionCartUrl(planItemsMap.get(card.getAttribute('data-plan')) || []);
    if (!cartUrl) return;
    event.preventDefault();
    window.location.assign(cartUrl);
  }, true);

  const variantIds = Array.from(new Set(Array.from(planItemsMap.values()).flat().map((item) => item.id)));

  const gidList = variantIds.map((id) => {
    try { return btoa(`gid://shopify/ProductVariant/${id}`); } catch (e) { return `gid://shopify/ProductVariant/${id}`; }
  });

  const query = `query VariantNodes($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        title
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
        sellingPlanAllocations(first: 10) {
          edges {
            node {
              sellingPlan { id name }
              priceAdjustments {
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }
        }
        product {
          title vendor productType description tags onlineStoreUrl
          featuredImage { url altText }
        }
      }
    }
  }`;

  showState('loading');

  fetch(`https://${storeDomain}/api/${apiVersion}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
    body: JSON.stringify({ query, variables: { ids: gidList } })
  })
    .then((res) => { if (!res.ok) throw new Error('Storefront API request failed'); return res.json(); })
    .then((payload) => {
      if (payload?.errors?.length) throw new Error('Storefront API returned errors');
      const nodes = payload?.data?.nodes || [];

      const variantMap = new Map();
      nodes.forEach((node) => {
        if (!node?.id) return;
        const match = node.id.match(/ProductVariant\/(\d+)/);
        if (match) variantMap.set(match[1], node);
      });

      let missingVariants = 0;

      cards.forEach((card) => {
        const plan = card.getAttribute('data-plan');
        if (!visiblePlans.includes(plan)) return;
        const planItems = planItemsMap.get(plan) || [];
        const listEl = card.querySelector('[data-mtu-items]');
        const regularPriceEl = card.querySelector('[data-mtu-regular-price]');
        const subPriceEl = card.querySelector('[data-mtu-sub-price]');
        const recurringPriceEl = card.querySelector('[data-mtu-recurring-price]');
        const frequencyEl = card.querySelector('[data-mtu-frequency]');
        const cartButton = card.querySelector('[data-mtu-cart-button]');
        let richDetailsUpdated = false;

        if (!listEl) return;
        listEl.innerHTML = '';

        let totalRegularPrice = 0;
        let totalSubPrice = 0;
        let totalRecurringPrice = 0;
        let currencyCode = 'JPY';
        let frequencyName = '毎月配達';

        planItems.forEach((item) => {
          const data = variantMap.get(item.id);
          if (!data) { missingVariants += 1; return; }

          const productTitle = data?.product?.title || '商品名未取得';
          const variantTitle = data?.title || '';
          const displayName = variantTitle && variantTitle !== 'Default Title'
            ? `${productTitle} - ${variantTitle}` : productTitle;
          const productType = isRich ? 'ワカサプリ' : (data?.product?.productType || data?.product?.vendor || 'ワカサプリ');
          const richMeta = isRich ? getRichSupplementMeta(productTitle, variantTitle) : null;
          const productDescription = richMeta
            ? richMeta.description
            : getSupplementDescription(productTitle, variantTitle, data?.product?.description);
          const imageUrl = data?.product?.featuredImage?.url || '';
          const imageAlt = data?.product?.featuredImage?.altText || displayName;
          currencyCode = data?.price?.currencyCode || currencyCode;

          if (isRich && !richDetailsUpdated) {
            setRichSupplementDetails(productTitle, variantTitle);
            richDetailsUpdated = true;
          }

          // 定期購入プランの価格を取得
          const allocations = data?.sellingPlanAllocations?.edges || [];
          const targetAllocation = findTargetAllocation(item.id, allocations);
          const targetSellingPlanId = getNumericSellingPlanId(targetAllocation?.node?.sellingPlan?.id);
          if (targetSellingPlanId) selectedSellingPlanIds.set(item.id, targetSellingPlanId);

          const fixedPrices = subscriptionPriceMap[item.id];
          const subPriceAmount = fixedPrices?.first
            || targetAllocation?.node?.priceAdjustments?.[0]?.price?.amount
            || data?.price?.amount;
          const compareAtPriceAmount = targetAllocation?.node?.priceAdjustments?.[0]?.compareAtPrice?.amount
            || data?.compareAtPrice?.amount;
          const regularPriceAmount = fixedPrices?.regular || compareAtPriceAmount || data?.price?.amount;
          const recurringPriceAmount = fixedPrices?.recurring || regularPriceAmount;

          if (subPriceAmount) totalSubPrice += Number(subPriceAmount) * item.qty;
          if (regularPriceAmount) totalRegularPrice += Number(regularPriceAmount) * item.qty;
          if (recurringPriceAmount) totalRecurringPrice += Number(recurringPriceAmount) * item.qty;

          if (targetAllocation?.node?.sellingPlan?.name) {
            frequencyName = targetAllocation.node.sellingPlan.name;
          }

          const li = document.createElement('li');
          li.className = 'mtu__item';
          const imageHtml = imageUrl
            ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" class="mtu__item-image">` : '';
          const productMainClass = imageUrl ? 'mtu__product-main' : 'mtu__product-main mtu__product-main--no-image';
          const productTagsHtml = isRich
            ? ``
            : `
                <span class="mtu__product-tag">医療機関専売品質</span>
                <span class="mtu__product-tag">高吸収率</span>
              `;
          const productLabel = isRich ? 'あなたにおすすめの食品' : 'あなたにおすすめの製品';

          li.innerHTML = `
            <div class="${productMainClass}">
              ${imageHtml}
              <div class="mtu__product-info">
                <div class="mtu__product-label">${productLabel}</div>
                <div class="mtu__product-brand">${escapeHtml(productType)}</div>
                <div class="mtu__product-title">${escapeHtml(displayName)}</div>
                <p class="mtu__product-desc">${escapeHtml(productDescription)}</p>
                <div class="mtu__product-tags">
                  ${productTagsHtml}
                </div>
              </div>
            </div>
          `;
          listEl.appendChild(li);
        });

        // 配送頻度表示
        if (frequencyEl) frequencyEl.textContent = frequencyName;

        // 価格表示
        const priceSuffix = '（送料込み）';

        if (regularPriceEl && totalRegularPrice > 0) {
          regularPriceEl.innerHTML = `${formatPrice(totalRegularPrice, currencyCode)}<span style="font-size:0.6em; font-weight:normal;">${priceSuffix}</span>`;
        }
        if (subPriceEl && totalSubPrice > 0) {
          subPriceEl.innerHTML = `${formatPrice(totalSubPrice, currencyCode)}<span style="font-size:0.6em; font-weight:normal;">${priceSuffix}</span>`;
        }
        if (recurringPriceEl && totalRecurringPrice > 0) {
          recurringPriceEl.innerHTML = `${formatPrice(totalRecurringPrice, currencyCode)}<span style="font-size:0.6em; font-weight:normal;">${priceSuffix}</span>`;
        }

        // カートURL
        if (cartButton && planItems.length > 0 && items.length < 3) {
          const cartUrl = buildSubscriptionCartUrl(planItems);
          if (cartUrl) {
            cartButton.setAttribute('href', cartUrl);
            cartButton.removeAttribute('aria-disabled');
          }
        }
      });

      if (missingVariants > 0 && errorEl) {
        errorEl.textContent = `商品情報を取得できないIDが${missingVariants}件あります。`;
        showState('error');
      } else {
        showState('ready');
      }
    })
    .catch((error) => {
      console.error('Storefront API error:', error);
      if (errorEl) errorEl.textContent = '商品情報の取得に失敗しました。';
      showState('error');
    });
})();
